import { useQuery } from "@apollo/client";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import {
  IdLinkComponent,
  SubmittalReportDetailsCellRenderer,
  dummyColumnDef,
  responsibleContractorCellRenderer,
  specSectionLabel,
  specSectionRenderer,
  truncatedTextCellRenderer,
  userOrgCellRenderer
} from "components/cell-renders";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import {
  useIntegrationProjectParticipants,
  useProjectParticipants
} from "hooks/project-participants";
import { updateSubmittalList } from "pages/submittals-list/helpers";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useParams } from "react-router";

import { QUERY_SUBMITTAL_TYPES } from "services/graphQL/queries";
import { Button, Dropdown, Menu, Popover } from "antd";
import { StartPrinting } from "utils/ag-grid-print-utils";
import { getUser } from "services/auth";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { MUTATION_UPDATE_AUDIT_EXPORTED_LOGS } from "services/graphQL/mutations";
import { getSubmittalFormattedValues } from "pages/reports-menu/upcoming-workflows/export-csv-util";
import { submittalSequenceComparator } from "pages/submittals-log-page/utils";
import CiqAgSearch from "components/ciq-ag-search";
import { CiqIconBox } from "components/svg-icons";
import { submittalStatusCellRendererV2Report } from "entity-app/shared-components/log-render-components/cell-renders";
import {
  AcceptanceStatus,
  AuditLogFeatures,
  AuditLogFeatureType,
  InfoMessages,
  ReportTitles,
  RiskStates,
  SubmittalStatusToStrMap
} from "../../constants";
import { DownloadReport } from "../../services/export-reports/download-report";
import { useCiqFeatureSubmittalRiskReportList } from "./hooks/risk-report";

type Props = {
  risk: RiskStates;
  isIntegrationMode: boolean;
  printMode?: boolean;
};

function SubmittalRiskDetail(props: Props) {
  const { risk, isIntegrationMode, printMode } = props;

  const { projectId } = useParams() as any;

  const gridRef = useRef<AgGridReact<any>>(null);

  const [expandedItemIds, setExpandedItemIds] = useState<string[]>([]);

  const [disableExport, setDisableExport] = useState(false);

  const {
    gqlClientForProject,
    projectDetails,

    columnHeaders: { submittalHeaderMap }
  }: TProjectContext = useContext(ProjectContext);

  const { projectParticipants: WFProjectParticipants } =
    useProjectParticipants(isIntegrationMode);

  const { data: INTGProjectParticipants } = useIntegrationProjectParticipants(
    !isIntegrationMode
  );

  const projectParticipants = isIntegrationMode
    ? INTGProjectParticipants
    : WFProjectParticipants;

  const { gridData } = useCiqFeatureSubmittalRiskReportList({
    risk_level: risk
  });

  const [saveExportAuditLogEvent] = useCIQMutation(
    MUTATION_UPDATE_AUDIT_EXPORTED_LOGS,
    {
      client: gqlClientForProject
    }
  );

  useEffect(() => {
    if (gridData.submittals) {
      setTimeout(() => {
        gridRef.current?.api.refreshCells({
          force: true,
          columns: ["assignee"]
        });
      }, 0);
    }
  }, [gridData.submittals]);

  const { data: submittalTypes } = useQuery(QUERY_SUBMITTAL_TYPES, {
    client: gqlClientForProject,
    skip: !gqlClientForProject
  });

  const typesMap = useMemo(() => {
    if (submittalTypes) {
      const tmpTypesMap: any = {};
      submittalTypes.configurations_value.forEach((type: any) => {
        tmpTypesMap[type.id] = type;
      });
      return tmpTypesMap;
    }
    return null;
  }, [submittalTypes]);

  const statusesMap: any = useMemo(() => {
    const tmpStatusesMap: any = {};

    Object.keys(SubmittalStatusToStrMap).forEach((keyStatusId: any) => {
      tmpStatusesMap[keyStatusId] = {
        name: SubmittalStatusToStrMap[keyStatusId]
      };
    });
    return tmpStatusesMap;
  }, []);

  const submittalListTableData: any = useMemo(() => {
    if (!gridData.submittals || !projectParticipants.allProjectUsersMap)
      return null;

    const updatedSubmittalList = updateSubmittalList(
      gridData.submittals,
      projectParticipants
    );
    return updatedSubmittalList;
  }, [projectParticipants, gridData.submittals]);

  const defaultColDef: {} = useMemo(() => {
    const defaultCol: any = {
      sortable: true,
      editable: false,
      filter: true,
      resizable: true,
      menuTabs: ["filterMenuTab"],
      sortingOrder: ["asc", "desc"],
      suppressColumnsToolPanel: false
    };
    if (printMode) {
      defaultCol.maxWidth = 155;
    }
    return defaultCol;
  }, [printMode]);

  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [gridData.submittals, projectParticipants]);

  const submittalListIds = useMemo(() => {
    if (!submittalListTableData) return [];
    return submittalListTableData.map((submittal: any) => {
      return submittal.id;
    });
  }, [submittalListTableData]);

  const columnDefs = useMemo<ColDef[]>(() => {
    return [
      dummyColumnDef,
      {
        colId: "submittal_id",
        field: "submittal_id",
        headerName: submittalHeaderMap?.submittal_id || "",
        headerTooltip: submittalHeaderMap?.submittal_id || "",
        filter: false,
        menuTabs: [],
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          innerRenderer: (params: any) => IdLinkComponent(params, true),
          projectId
        },
        comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) =>
          submittalSequenceComparator(
            valueA,
            valueB,
            nodeA,
            nodeB,
            projectDetails?.spec_section
          ),
        tooltipValueGetter: (params: any) => {
          const cellText =
            params.context.projectDetails?.spec_section && params.data.spec_no
              ? `${params.data.spec_no} - ${params.value}`
              : `${params.value}`;
          return cellText;
        }
      },
      {
        colId: "custom_spec_section",
        headerName: submittalHeaderMap?.spec_no || "",
        headerTooltip: submittalHeaderMap?.spec_no || "",
        cellRenderer: (params: any) => specSectionRenderer(params, false),
        cellRendererParams: {
          specNoField: "spec_no",
          specNameField: "spec_name",
          projectId
        },
        valueGetter: (params: any) => {
          try {
            const specSectionStr = specSectionLabel({
              specNumber: params.data.spec_no,
              specName: params.data.spec_name
            });

            return specSectionStr;
          } catch (ex) {
            return "";
          }
        },
        comparator: (valueA: string | null, valueB: string | null) => {
          if (!valueA) return 1;
          if (!valueB) return -1;
          return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
        }
      },
      {
        colId: "title",
        field: "title",
        headerName: submittalHeaderMap?.title || "",
        headerTooltip: submittalHeaderMap?.title || "",
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "type",
        field: "type",
        headerName: submittalHeaderMap?.type || "",
        headerTooltip: submittalHeaderMap?.type || "",
        keyCreator: (params: any) => params.value,
        getQuickFilterText: (params: any) => params.value,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "responsible_contractor",
        field: "responsible_contractor_id",
        headerName: submittalHeaderMap?.responsible_contractor || "",
        headerTooltip: submittalHeaderMap?.responsible_contractor || "",
        sort: "asc",
        cellRenderer: (params: any) => {
          const data = responsibleContractorCellRenderer(params);
          return data.cellMarkup;
        },
        valueGetter: (params: any) => {
          const data = responsibleContractorCellRenderer(params);
          return data.orgName;
        },
        cellStyle: (params) => {
          if (params.data.status_id === AcceptanceStatus.ACTIVE) {
            return { opacity: 0.5 };
          }
          return { opacity: 1.0 };
        }
      },
      {
        colId: "assignee",
        headerName: submittalHeaderMap?.assignee || "",
        headerTooltip: submittalHeaderMap?.assignee || "",
        cellRenderer: (params: any) => {
          const data = userOrgCellRenderer(params, false);
          return data.cellMarkup;
        },
        cellRendererParams: {
          userInput: {
            id: "assignee_user_id",
            firstName: "assignee_first_name",
            lastName: "assignee_last_name",
            org: "assignee_org",
            unregistered_user: "assignee_unregistered",
            unregistered_user_org: "assignee_unregistered_org",
            statusKey: "assignee_status"
          }
        },
        valueGetter: (params: any) => {
          const data = userOrgCellRenderer(params, false);
          return data.fullName;
        },
        cellStyle: (params) => {
          if (params.data.status_id === AcceptanceStatus.ACTIVE) {
            return { opacity: 0.5 };
          }
          return { opacity: 1.0 };
        }
      },
      {
        colId: "status",
        field: "workflow_status",
        headerName: submittalHeaderMap?.workflow_status || "",
        headerTooltip: submittalHeaderMap?.workflow_status || "",
        maxWidth: 161,
        cellRenderer: (params: any) =>
          submittalStatusCellRendererV2Report(params),
        cellRendererParams: {
          dataMapVar: "statusesMap",
          valueVar: "id",
          labelVar: "name"
        }
      },
      {
        colId: "risk_level",
        field: "risk_level",
        headerName: "RISK",
        hide: true
      },
      {
        colId: "risk_assessment",
        headerName: "RISK ASSESSMENT",
        hide: true
      },
      {
        colId: "linked_materials",
        field: "submittal_material_links",
        headerName: "IMPACTED MATERIALS",
        hide: true
      },
      {
        colId: "linked_tasks",
        field: "submittal_schedule_links",
        headerName: "IMPACTED ACTIVITIES",
        hide: true
      }
    ];
  }, [projectDetails, projectId, submittalHeaderMap]);

  const expandCollapseAll = useCallback(() => {
    let tmpExpandedItemIds = [];
    if (
      expandedItemIds.length &&
      expandedItemIds.length === submittalListIds.length
    ) {
      tmpExpandedItemIds = [];
    } else {
      tmpExpandedItemIds = submittalListIds;
    }

    gridRef?.current?.api?.forEachNode((node: any) => {
      // eslint-disable-next-line no-param-reassign
      node.expanded = !!tmpExpandedItemIds.length;
    });
    gridRef?.current?.api?.onGroupExpandedOrCollapsed();

    setExpandedItemIds(tmpExpandedItemIds);
  }, [expandedItemIds.length, submittalListIds]);

  const saveExportAuditLog = useCallback(async () => {
    const payload: any = {
      variables: {
        eventType: AuditLogFeatureType.LOG_EXPORTED,
        feature: AuditLogFeatures.SUBMITTAL_RISK_REPORT
      }
    };
    await saveExportAuditLogEvent(payload);
  }, [saveExportAuditLogEvent]);

  const exportAsPDF = useCallback(async () => {
    setDisableExport(true);

    const res: any = await DownloadReport(
      `/project/${projectId}/reports/submittals/export?subscriptionId=#{subscriptionId}&code=#{code}`,
      ReportTitles.submittalsRiskReport,
      getUser(),
      projectDetails
    );
    if (res?.data) {
      saveExportAuditLog();
    }

    setDisableExport(false);
  }, [projectDetails, projectId, saveExportAuditLog]);

  const gridContext: any = useMemo(() => {
    return {
      projectDetails,
      typesMap,
      statusesMap,
      isIntegrationMode,
      projectParticipants
    };
  }, [
    isIntegrationMode,
    projectDetails,
    projectParticipants,
    statusesMap,
    typesMap
  ]);

  const detailCellRenderer = useMemo(() => {
    return SubmittalReportDetailsCellRenderer;
  }, []);

  /**
   * Start the jsreport print after finishing the loading of all records in ag-grid.
   *
   * Here we are trying to get the number of rows from the Ag-Grid which is rendered and
   * the number of record from the data. Once it is matched it means grid
   * has been rendered all records. After this condition match starting the print.
   */
  useEffect(() => {
    let intervalTimer: string | number | NodeJS.Timeout | undefined;
    if (printMode) {
      const totalRecords = submittalListTableData?.length;
      // eslint-disable-next-line prefer-const
      intervalTimer = setInterval(() => {
        let totalRowCount = gridRef.current?.api?.getDisplayedRowCount() || 0;
        totalRowCount /= 2; // there is grouping of row hence devide by two.

        // Stop the timer once all records are diplayed in the grid row.
        if (totalRowCount === totalRecords) {
          clearInterval(intervalTimer);
          setTimeout(StartPrinting, 100);
        }
      }, 500);
    }
    return () => clearInterval(intervalTimer);
  }, [submittalListTableData, printMode, submittalListIds]);

  const updateExpandedItems = (itemId: string) => {
    if (expandedItemIds.includes(itemId)) {
      const itemRemovedArray: any = expandedItemIds.filter(
        (id: any) => id !== itemId
      );
      setExpandedItemIds(itemRemovedArray);
    } else {
      const clonedExpIds = [...expandedItemIds];
      clonedExpIds.push(itemId);
      setExpandedItemIds(clonedExpIds);
    }
  };

  const exportPdfButton = useMemo(() => {
    const exportBtnDisabled =
      !submittalListTableData || !submittalListTableData.length;

    if (exportBtnDisabled)
      return (
        <Menu.Item onClick={exportAsPDF} disabled={exportBtnDisabled}>
          <span>{disableExport ? "Exporting" : "Export PDF"}</span>
        </Menu.Item>
      );

    const popoverContent = (
      <div className="w-[310px]">{InfoMessages.exportReportToPdfTooltip}</div>
    );
    return (
      <Menu.Item onClick={exportAsPDF} disabled={exportBtnDisabled}>
        <Popover content={popoverContent} trigger="hover" placement="topRight">
          <span>{disableExport ? "Exporting" : "Export PDF"}</span>
        </Popover>
      </Menu.Item>
    );
  }, [disableExport, exportAsPDF, submittalListTableData]);

  const exportLogAsCSV = () => {
    const api = gridRef.current?.api;
    const additionalColumns = [
      "risk_level",
      "risk_assessment",
      "linked_materials",
      "linked_tasks"
    ];
    if (gridRef.current && api)
      gridRef.current?.api?.exportDataAsCsv({
        columnKeys: api
          .getAllGridColumns()
          ?.filter((col: any) => {
            const colId = col.getColId();
            return col.isVisible() || additionalColumns?.includes(colId);
          })
          .map((col: any) => col.getColId()),
        processCellCallback(params: any): string {
          return getSubmittalFormattedValues(params);
        },
        fileName: `Submittals at ${risk} Risk_${projectDetails?.name}.csv`
      });
  };

  return (
    <div>
      <div className="pb-3 pt-5 px-6 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <CiqIconBox feature="SUBMITTALS" />
          <div className="flex text-base font-semibold">
            Submittals at <div className="camelCase px-1">{risk}</div> Risk
          </div>
        </div>
        <div className="flex items-center gap-x-3">
          <CiqAgSearch
            key="RiskSubmittals_search"
            gridRef={gridRef}
            placeholder="Search Submittals"
            data-testid="risk-submittals-search"
          />

          <Button
            onClick={() => {
              expandCollapseAll();
            }}
            className="w-[110px] hide-on-print"
            disabled={!submittalListTableData || !submittalListTableData.length}
            data-testid="risk-submittals-expand-toggle"
          >
            {expandedItemIds.length &&
            expandedItemIds.length === submittalListIds.length ? (
              <span>Collapse All</span>
            ) : (
              <span>Expand All</span>
            )}
          </Button>

          <Dropdown
            className="w-[110px]  hide-on-print"
            data-testid="risk-submittals-export-dropdown"
            overlay={
              <Menu>
                {exportPdfButton}
                <Menu.Item
                  onClick={exportLogAsCSV}
                  disabled={
                    !submittalListTableData || !submittalListTableData.length
                  }
                  data-testid="risk-submittals-export-csv"
                >
                  Export CSV
                </Menu.Item>
              </Menu>
            }
            placement="bottom"
          >
            <Button
              loading={disableExport}
              data-testid="risk-submittals-export-button"
            >
              Export
            </Button>
          </Dropdown>
        </div>
      </div>
      <div style={{ height: `${printMode ? "auto" : "calc(100vh - 325px)"}` }}>
        <AgGridReact
          ref={gridRef}
          className="ag-theme-alpine"
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={submittalListTableData}
          headerHeight={36}
          rowHeight={62}
          pagination={false}
          context={gridContext}
          suppressPaginationPanel
          suppressContextMenu
          masterDetail
          detailCellRenderer={detailCellRenderer}
          domLayout={printMode ? "print" : "normal"}
          groupDefaultExpanded={printMode ? 1 : 0}
          onRowGroupOpened={(event: any) => {
            updateExpandedItems(event.data.id);
          }}
          tooltipShowMode="whenTruncated"
        />
      </div>
    </div>
  );
}

export default SubmittalRiskDetail;
